import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { doc, Firestore, getDoc } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  StripeFactoryService,
  StripeInstance,
  StripePaymentElementComponent
} from 'ngx-stripe';
import { Subscription } from 'rxjs';
import { StripeElementsOptions } from '@stripe/stripe-js';
import { TranslateService } from '@ngx-translate/core';

import { environment } from '../../environments/environment';

@Component({
    selector: 'app-payment-init',
    templateUrl: `./payment-init.component${environment.componentAssetExt}.html`,
    styleUrl: `./payment-init.component${environment.componentAssetExt}.scss`,
    standalone: false
})
export class PaymentInitComponent implements OnDestroy, OnInit {

  @ViewChild(StripePaymentElementComponent)
  paymentElement!: StripePaymentElementComponent;

  giveMoreData = false;
  inProgress = false;
  paymentSucceeded = false;
  stripe: StripeInstance;
  stripeElementsOptions?: StripeElementsOptions;

  private piSubscription?: Subscription;
  private session: any;
  private sessionId = '';

  constructor(
    factoryService: StripeFactoryService,
    private firestore: Firestore,
    private functions: Functions,
    private route: ActivatedRoute,
    private router: Router,
    translate: TranslateService
  ) {
    translate.use(translate.getBrowserLang() ?? translate.getFallbackLang() ?? 'en');
    this.stripe = factoryService.create();
  }

  async ngOnInit() {
    const sid = this.route.snapshot.paramMap.get('sid');
    if (sid === null) return;
    this.sessionId = sid;

    const sessionDS = await getDoc(doc(
      this.firestore,
      `instances/${environment.instanceId}/sessions/${this.sessionId}`
    ));
    this.session = sessionDS.data();

    this.piSubscription = this.stripe
      .retrievePaymentIntent(this.session.paymentIntent.client_secret)
      .subscribe(result => {
        this.piSubscription?.unsubscribe();
        this.piSubscription = undefined;

        // Retreiving payment intent failed
        if (result.error) {
          console.error(result.error);
        }

        // Not payed (normal flow)
        else if (result.paymentIntent.status !== 'succeeded') {
          this.stripeElementsOptions = {
            clientSecret: this.session.paymentIntent.client_secret
          }
        }

        // Payed, but no package was generated (resiliency flow)
        else if (this.session.status === 'reserved') {
          this.paymentSucceeded = true;
        }

        // Payed, lease already in progress
        else {
          this.router.navigate(['box-open', this.sessionId]);
        }
      });
  }

  ngOnDestroy(): void {
    this.piSubscription?.unsubscribe();
  }

  pay() {
    this.inProgress = true;
    this.piSubscription = this.stripe.confirmPayment({
      elements: this.paymentElement.elements,
      redirect: 'if_required'
    }).subscribe(async result => {
      this.piSubscription?.unsubscribe();
      this.piSubscription = undefined;

      if (result.error) {
        console.error(result);
      }
      else if (result.paymentIntent.status === 'succeeded') {
        await this.finalizeReservation();
      }
      else {
        console.log(result);
      }
      this.inProgress = false;
    });
  }

  async finalizeReservation() {
    this.inProgress = true;
    await httpsCallable(this.functions, 'finalizeReservation')({
      instanceId: environment.instanceId,
      sessionId: this.sessionId
    }).then(
      () => this.router.navigate(['box-open', this.sessionId])
    ).catch(reason => {
      console.error(reason);
      this.paymentSucceeded = true;
    });
    this.inProgress = false;
  }
}
